import { Badge, Button, P, Toggle } from '@fashionnovateam/nova-ui-toolkit';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import CounterInformation from './CounterInformation';
import QuantityForm from './forms/QuantityForm';
import InfoForm from './forms/InfoForm';
import { GarmentSection } from './forms/GarmentSection';
import { ControlledFormInput } from '../../../Lib/Utils/FormUtils/ControlledFormInput';
import { useCounterContext } from '../hooks/useCounter';
import {
  CounterQuoteStatus,
  counterQuoteStatusColorMapping,
  counterQuoteStatusDisplayMapping,
  CounterQuoteStatusToHide,
  CounterFabricFormValues,
  GarmentFabricDetails,
  CounterFiberContent,
  SecondaryFabric,
} from '../types';

function QuoteTab() {
  const { selectedCounter } = useCounterContext();
  const { control, watch, setValue } =
    useFormContext<CounterFabricFormValues>();

  const isMultiGarment = watch('isMultiGarment');
  const garments = watch('garments');

  const quoteStatusToHide = Object.values(CounterQuoteStatusToHide) as string[];
  const shouldHideQuoteStatus = quoteStatusToHide.includes(
    selectedCounter?.counter_quote_status as string,
  );

  const quoteStatus = shouldHideQuoteStatus
    ? selectedCounter?.prev_counter_quote_status
    : (selectedCounter?.counter_quote_status as CounterQuoteStatus);

  let mappedStatus =
    counterQuoteStatusDisplayMapping[
      quoteStatus as keyof typeof counterQuoteStatusDisplayMapping
    ];

  if (shouldHideQuoteStatus) {
    mappedStatus = selectedCounter?.counter_quote
      ? 'Quote Sent'
      : 'Quote Not Sent';
  }

  const formEnabled = [
    CounterQuoteStatus.NOT_SENT,
    CounterQuoteStatus.SENT,
  ].includes(mappedStatus as CounterQuoteStatus);

  const createNewFiber = (): CounterFiberContent => ({
    id: `fiber-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    name: '',
    percentage: 0,
  });

  const createDefaultGarment = (
    garmentIndex: number,
    part: 'top' | 'bottom' | 'other' = 'other',
  ): GarmentFabricDetails => ({
    garmentIndex,
    garmentPart: part,
    fabricContentList: [createNewFiber()],
    fabricWeight: undefined,
    fabricCodeName: '',
    isFabricWeightNotApplicable: false,
    hasPolyurethaneCoating: false,
    secondaryFabrics: [],
  });

  const collectLegacySecondaryFabrics = (): SecondaryFabric[] => {
    const secondaryFabrics: SecondaryFabric[] = [];
    const contrast = watch('contrast');
    const lining = watch('lining');
    const filling = watch('filling');
    // Legacy-only types (can view/edit/delete existing, but can't add new)
    const backing = watch('backing');
    const coating = watch('coating');

    if (lining?.liningContent?.length) {
      secondaryFabrics.push({
        type: 'lining',
        instanceIndex: 1,
        content: lining.liningContent,
        weight: lining.liningWeight ? Number(lining.liningWeight) : undefined,
      });
    }
    if (contrast?.contrastContent?.length) {
      secondaryFabrics.push({
        type: 'contrast',
        instanceIndex: 1,
        content: contrast.contrastContent,
        weight: contrast.contrastWeight
          ? Number(contrast.contrastWeight)
          : undefined,
      });
    }
    if (filling?.fillingContent?.length) {
      secondaryFabrics.push({
        type: 'filling',
        instanceIndex: 1,
        content: filling.fillingContent,
        weight: filling.fillingWeight
          ? Number(filling.fillingWeight)
          : undefined,
      });
    }
    if (backing?.backingContent?.length) {
      secondaryFabrics.push({
        type: 'backing',
        instanceIndex: 1,
        content: backing.backingContent,
        weight: backing.backingWeight
          ? Number(backing.backingWeight)
          : undefined,
      });
    }
    if (coating?.coatingContent?.length) {
      secondaryFabrics.push({
        type: 'coating',
        instanceIndex: 1,
        content: coating.coatingContent,
        weight: coating.coatingWeight
          ? Number(coating.coatingWeight)
          : undefined,
      });
    }
    return secondaryFabrics;
  };

  const clearLegacySecondaryFabrics = () => {
    setValue('contrast', undefined);
    setValue('lining', undefined);
    setValue('filling', undefined);
    setValue('backing', undefined);
    setValue('coating', undefined);
  };

  const handleMultiGarmentToggle = (checked: boolean) => {
    setValue('isMultiGarment', checked);
    if (checked) {
      const currentFabricContentList = watch('fabric_content_list') || [];
      const currentWeight = watch('weight');
      const currentFabricCodeName = watch('fabric_code_name') || '';
      const currentWeightNA = watch('isFabricWeightNotApplicable') ?? false;
      const currentHasPU = watch('hasPolyurethaneCoating') ?? false;

      const legacySecondaryFabrics = collectLegacySecondaryFabrics();
      const currentSecondaryFabrics = watch('secondaryFabrics') || [];
      const allSecondaryFabrics = [
        ...currentSecondaryFabrics,
        ...legacySecondaryFabrics,
      ];

      const firstGarmentIndex = garments?.[0]?.garmentIndex ?? 1;
      const firstGarmentFromFabricDetails: GarmentFabricDetails = {
        garmentIndex: firstGarmentIndex,
        garmentPart: garments?.[0]?.garmentPart ?? 'top',
        fabricContentList:
          currentFabricContentList.length > 0
            ? currentFabricContentList
            : [createNewFiber()],
        fabricWeight:
          currentWeight === Infinity || currentWeight === undefined
            ? undefined
            : String(currentWeight),
        fabricCodeName: currentFabricCodeName,
        isFabricWeightNotApplicable: currentWeightNA,
        hasPolyurethaneCoating: currentHasPU,
        secondaryFabrics:
          allSecondaryFabrics.length > 0
            ? allSecondaryFabrics
            : garments?.[0]?.secondaryFabrics || [],
      };

      clearLegacySecondaryFabrics();
      setValue('secondaryFabrics', []);

      if (garments && garments.length >= 2) {
        setValue('garments', [
          firstGarmentFromFabricDetails,
          ...garments.slice(1),
        ]);
      } else {
        // Calculate second garment index to ensure uniqueness
        const secondGarmentIndex = firstGarmentIndex + 1;
        setValue('garments', [
          firstGarmentFromFabricDetails,
          createDefaultGarment(secondGarmentIndex, 'bottom'),
        ]);
      }
    } else if (garments && garments.length > 0) {
      const firstGarment = garments[0];
      if (firstGarment.fabricContentList?.length > 0) {
        setValue('fabric_content_list', firstGarment.fabricContentList);
      }
      if (firstGarment.fabricWeight !== undefined) {
        setValue('weight', firstGarment.fabricWeight);
      }
      if (firstGarment.fabricCodeName) {
        setValue('fabric_code_name', firstGarment.fabricCodeName);
      }
      setValue(
        'isFabricWeightNotApplicable',
        firstGarment.isFabricWeightNotApplicable ?? false,
      );
      setValue(
        'hasPolyurethaneCoating',
        firstGarment.hasPolyurethaneCoating ?? false,
      );

      setValue('secondaryFabrics', firstGarment.secondaryFabrics || []);
      clearLegacySecondaryFabrics();
    }
  };

  const addGarment = () => {
    const currentGarments = garments || [];
    const nextIndex =
      currentGarments.length > 0
        ? Math.max(...currentGarments.map((g) => g.garmentIndex)) + 1
        : 1;
    setValue('garments', [
      ...currentGarments,
      createDefaultGarment(nextIndex, 'other'),
    ]);
  };

  return (
    <div className="flex flex-col gap-2 py-2 bg-neutral-100 rounded-2xl flex-grow overflow-y-auto">
      <CounterInformation />
      <div className="py-4 px-5 bg-neutral-0 flex flex-col gap-5">
        <div className="flex justify-between items-center">
          <P size="sm" weight="medium">
            Your Quote
          </P>
          <Badge
            color={
              counterQuoteStatusColorMapping[
                mappedStatus as keyof typeof counterQuoteStatusColorMapping
              ] ?? 'default'
            }
            contrast="low"
          >
            {mappedStatus}
          </Badge>
        </div>
        <QuantityForm />
        <InfoForm />

        <div className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-neutral">
          <Toggle
            checked={isMultiGarment || false}
            onChange={(e) => handleMultiGarmentToggle(e.target.checked)}
            disabled={!formEnabled}
            className="!scale-75"
          />
          <P size="xsm" color="primary">
            This garment contains multiple pieces with different fabric contents
          </P>
        </div>

        {isMultiGarment ? (
          <>
            <div className="rounded-xl border border-neutral-200 p-5 bg-white">
              <ControlledFormInput
                control={control}
                name="fabric_type"
                label="Fabric Type"
                inputType="text"
                required
                disabled={!formEnabled}
              />
            </div>
            {(garments || []).map((garment, index) => (
              <GarmentSection
                key={garment.garmentIndex}
                garment={garment}
                index={index}
                canRemove={(garments?.length || 0) > 2}
              />
            ))}
            {formEnabled && (
              <Button
                type="button"
                size="md"
                color="primary"
                onClick={addGarment}
                className="w-fit"
              >
                Add Another Garment
              </Button>
            )}
          </>
        ) : (
          <GarmentSection />
        )}
      </div>
      <div className="px-5">
        <Button disabled={!formEnabled} type="submit" className="w-full">
          Save Quote
        </Button>
      </div>
    </div>
  );
}

export default QuoteTab;
